import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { PipelineStage } from "@school-sales-ai/shared";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const REASON_MAX = 255;

type Props = {
  stage: PipelineStage;
  leadId: string;
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

function useCreateNote(
  leadId: string,
): ReturnType<typeof useMutation<void, Error, string>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (content: string) =>
      api
        .post<unknown>(`/crm/leads/${leadId}/notes`, { content })
        .then(() => undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notes", leadId] });
    },
  });
}

// Shown when a lead is moved into a column flagged `requiresReason`. Captures a
// free-text reason, saves it as an internal note (prefixed with the column
// label), then confirms the move. Moving here never pauses the bot.
export function StageReasonModal({
  stage,
  leadId,
  open,
  onConfirm,
  onCancel,
}: Props): React.JSX.Element {
  const [reason, setReason] = useState("");
  const createNote = useCreateNote(leadId);

  async function handleConfirm(): Promise<void> {
    const trimmed = reason.trim();
    if (!trimmed) return;
    await createNote.mutateAsync(`${stage.label}: ${trimmed}`.slice(0, REASON_MAX));
    toast.success("Razón guardada como nota");
    onConfirm();
    setReason("");
  }

  function handleCancel(): void {
    setReason("");
    onCancel();
  }

  const isConfirmDisabled = !reason.trim() || createNote.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleCancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mover a “{stage.label}”</DialogTitle>
          <DialogDescription>
            Escribe una razón. Se guardará como nota interna en el lead.
          </DialogDescription>
        </DialogHeader>

        <textarea
          value={reason}
          maxLength={REASON_MAX}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="Motivo…"
          className="w-full resize-none rounded-[10px] border border-ink-200 px-3 py-2 text-sm text-ink-800 focus:border-moss focus:outline-none"
        />

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancelar
          </Button>
          <Button
            onClick={() => void handleConfirm()}
            disabled={isConfirmDisabled}
          >
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
